import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot
} from '@angular/router';
import {EMPTY, Observable, of} from 'rxjs';
import {EventDetail} from "@biletix/core/models/event/event-detail.model";
import {RouteParameter} from "@biletix/core/common/constants/route-parameter.const";
import {catchError, map, mergeMap, take} from "rxjs/operators";
import {ApiResponseStatus} from "@biletix/core/models/api-response-status";
import {ApiResponse} from "@biletix/core/models/api-response";
import {EventService} from "@biletix/core/services/event.service";
import {GlobalErrorHandler} from "@biletix/core/services/global-error-handler.service";
import {NotificationService} from "@biletix/core/services/notification.service";
import {Location} from "@angular/common";
import {LocalStorageService} from "@biletix/core/services/local-storage.service";
import {RegionService} from "@biletix/core/services/region.service";
import {I18nService} from "@biletix/core/services/i18n.service";
import {EventPageStateService} from "@biletix/features/event/services/event-page-state.service";
import { StatusCodeResponseService } from '@biletix/core/services/status-code-response.service';
import { BtxPerformanceListItem } from '@biletix/core/models/event/performance-list-item.model';
import { isPlatformServer } from '@angular/common';
import { isTransientHttpError } from '@biletix/core/common/helper/http-error-classifier';

@Injectable({
  providedIn: 'root'
})
export class EventDetailResolver implements Resolve<EventDetail> {

  /**
   * Son performans tarihi bu süreden daha eskiyse etkinlik "geçmiş" sayılır.
   * Yeni bitmiş etkinlikler kısa süre daha kendi sayfasında kalır: kullanıcı
   * bilet/iade bilgisine erişebilsin ve arama sonuçları anında kopmasın.
   */
  private static readonly PAST_EVENT_GRACE_PERIOD_MS = 2 * 24 * 60 * 60 * 1000;

  private readonly isServer: boolean;

  constructor(private eventService: EventService,
              private eventPageStateService: EventPageStateService,
              private globalErrorHandler: GlobalErrorHandler,
              private statusCodeResponseService:StatusCodeResponseService,
              @Inject(PLATFORM_ID) private platformId: string) {
    this.isServer = isPlatformServer(platformId);
  }

  resolve(route: ActivatedRouteSnapshot,
          state: RouterStateSnapshot): Observable<EventDetail> {
    let eventCode:string = route.params[RouteParameter.PARAMS.EVENT_CODE];
    eventCode =eventCode.toUpperCase();
    let transientFailure = false;
    return this.eventService.findByEventCode(eventCode)
      .pipe(
        catchError((err) => {
          console.error('[findByEventCode] Error:', {
            message: err?.message,
            status: err?.status,
            statusText: err?.statusText,
            url: err?.url,
            stack: err?.stack,
            error: err?.error,
            name: err?.name,
          });
          this.globalErrorHandler.handleError(err);
          // Sunucuda anlık teknik hata (5xx/timeout/network) 503 döner, performans resolver'ı ile
          // aynı. EMPTY navigasyonu iptal eder ve @angular/ssr Express'in 404'üne düşer: geçici
          // bir API arızası, aktif bir etkinliğin URL'ini Google'a "yok" diye bildirirdi.
          if (this.isServer && isTransientHttpError(err)) {
            transientFailure = true;
            this.eventPageStateService.setCurrentEventEnded(false);
            this.statusCodeResponseService.setServiceUnavailable();
            return of(null as unknown as ApiResponse<EventDetail>);
          }
          // this.goBack();
          return EMPTY;
        }),
        take(1),
        mergeMap(response => {
          if (transientFailure) {
            return of(null as unknown as EventDetail);
          }
          //console.log("findByEventCode :");
          //console.log(response);
          if (response && response.status == ApiResponseStatus.SUCCESS) {
            this.eventPageStateService.setCurrentEventDetail(response.data);
            return this.eventService.getPerformancesByEventCode(eventCode).pipe(
              take(1),
              map(performances => {
                  this.eventPageStateService.setCurrentPerformanceList(performances);

                  this.markPastEvent(performances, response.data);

                  return response.data; // İlk API çağrısından gelen `EventDetail` verisi döndürülüyor
              }),
              catchError((err) => {
                  console.log("Error happened while calling getPerformancesByEventCode" + err);
                  this.globalErrorHandler.handleError(err);
                  // Performans listesi alınamadıysa etkinliğin bitip bitmediği
                  // bilinmiyor: 410 vermek yerine sayfa normal render edilir.
                  this.eventPageStateService.setCurrentEventEnded(false);
                  return of(response.data); // Hata durumunda ilk API yanıtı yine de döndürülüyor
              })
          );
          } else { // eventCode not found
            this.eventPageStateService.setCurrentEventEnded(false);
            this.statusCodeResponseService.setNotFound();
            /*
            this.notificationService.error(response.errors).onShown
              .pipe(take(1))
              .subscribe(() => {
                // this.goBack();
              });
            */
           //this.router.navigateByUrl("/404.btx");

            return of(response.data);
          }
        }));
  }

  /**
   * Süresi geçmiş etkinliği işaretler ve sunucuda yanıtı 410 Gone yapar.
   *
   * Neden 301 DEĞİL (önceki davranış sanatçı sayfasına 301 veriyordu): kullanıcı
   * bir etkinlik URL'ine geliyor ve o etkinlik gerçekten yok; başka bir sayfaya
   * yönlendirmek arama motoru için "bu URL taşındı" demektir, oysa içerik
   * kalıcı olarak kaldırılmıştır. 410, Google'ın URL'i index'ten hızla
   * düşürmesini sağlar ve yeniden taramayı bırakır. Kullanıcıyı yönlendirmek
   * yerine aynı URL'de "etkinlik sona erdi" görünümü render edilir; sanatçının
   * diğer etkinlikleri ve kategori bağlantısı orada sunulur
   * (bkz. event.component.html).
   *
   * Neden 404 DEĞİL: etkinlik hiç var olmamış değil, sona ermiştir. 404 var
   * olmayan `eventCode` için ayrılmıştır (bkz. `resolve` içindeki else dalı).
   *
   * Navigasyon İPTAL EDİLMEZ: `EMPTY` dönmek veya `RedirectCommand` üretmek
   * @angular/ssr'da 410 yanıtının gövdesiz kalmasına ya da Express'in kendi
   * 404'üne düşmesine yol açar (bkz. CLAUDE.md · SSR redirects and status codes).
   */
  private markPastEvent(performances: BtxPerformanceListItem[],
                        eventDetail: EventDetail): void {
    const isPastEvent = this.isPastEvent(performances, eventDetail);
    this.eventPageStateService.setCurrentEventEnded(isPastEvent);

    if (isPastEvent && this.isServer) {
      this.statusCodeResponseService.setGone();
    }
  }

  private isPastEvent(performances: BtxPerformanceListItem[], eventDetail: EventDetail): boolean {
    // `null`/`undefined` "veri gelmedi" demektir ve geçmiş sayılmamalıdır.
    // BOŞ DİZİ ise farklıdır: getPerformanceList geçmiş etkinlikler için
    // gerçekten `[]` döndürür (canlı: 4POEG), yani satılabilir performans
    // kalmamıştır. Bu ayrım korunmazsa ana senaryo hiç yönlendirilmez.
    if (!performances) {
      return false;
    }

    if (!performances.every(perf => perf.status === 's13_old_over')) {
      return false;
    }

    if (!eventDetail?.lastPerformanceDate) {
      return true;
    }

    const endDate = new Date(eventDetail.lastPerformanceDate).getTime();
    if (Number.isNaN(endDate)) {
      return true;
    }

    return Date.now() - endDate >= EventDetailResolver.PAST_EVENT_GRACE_PERIOD_MS;
  }
}
